import { OrderQuotePurpose, useUpdateOrderQuoteMutation } from '@codezo/graphql';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { DatePicker } from '../../ui/date-picker';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { SelectPopover } from '../../ui/select-popover';
import { Textarea } from '../../ui/textarea';
import { OrganizationSelector } from '../../selectors/OrganizationSelector';

const PURPOSE_OPTIONS = [
	{ value: OrderQuotePurpose.NotSpecified, label: 'Not specified' },
	{ value: OrderQuotePurpose.Packaging, label: 'Packaging' },
	{ value: OrderQuotePurpose.Event, label: 'Event' },
	{ value: OrderQuotePurpose.Resell, label: 'Resell' },
	{ value: OrderQuotePurpose.Other, label: 'Other' },
];

interface EditOrderQuoteDetailsModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	quoteId: string;
	initialValues: {
		name: string;
		description?: string | null;
		purpose?: string | null;
		/** Free text behind purpose OTHER — the page renders it beside the purpose. */
		purposeOther?: string | null;
		deadline?: string | null;
		/**
		 * Who the quote is for. Editable, but only until it has offers — see
		 * below. Nullable in the schema, so a quote can arrive here without one;
		 * that is the one case the lock must not apply to.
		 */
		organizationId?: string | null;
		organizationName?: string | null;
	};
	/**
	 * Offer batches authored on this quote. Each one is priced against the current
	 * client's payment settings and currency, so moving the quote afterwards leaves
	 * those figures quoted on a basis that no longer applies.
	 */
	offerBatchCount?: number | null;
	onSuccess: () => void;
}

export function EditOrderQuoteDetailsModal({
	open,
	onOpenChange,
	quoteId,
	initialValues,
	offerBatchCount,
	onSuccess,
}: EditOrderQuoteDetailsModalProps) {
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [purpose, setPurpose] = useState('');
	const [purposeOther, setPurposeOther] = useState('');
	const [deadline, setDeadline] = useState<Date | null>(null);
	const [organizationId, setOrganizationId] = useState('');

	const [updateQuote, { loading }] = useUpdateOrderQuoteMutation();

	useEffect(() => {
		if (open) {
			setName(initialValues.name);
			setDescription(initialValues.description || '');
			setPurpose(initialValues.purpose || '');
			setPurposeOther(initialValues.purposeOther || '');
			setDeadline(initialValues.deadline ? new Date(initialValues.deadline) : null);
			setOrganizationId(initialValues.organizationId ?? '');
		}
	}, [open, initialValues]);

	/* Locked once anything has been priced. Rendered disabled with the reason
	   rather than hidden — "you can't change this here, and here's why" is a
	   different and more useful answer than the field not existing at all, which
	   is what this modal said before. */
	const clientLocked = !!initialValues.organizationId && (offerBatchCount ?? 0) > 0;

	const handleSubmit = async () => {
		if (!name.trim() || !organizationId) return;
		try {
			await updateQuote({
				variables: {
					id: quoteId,
					input: {
						name: name.trim(),
						// Always sent, like purposeOther below: clearing it has to reach
						// the API, and it had its own modal only because this one didn't
						// carry it.
						description: description.trim(),
						purpose: purpose ? (purpose as OrderQuotePurpose) : undefined,
						// Always sent, unlike on create: clearing it has to reach the
						// API, and the resolver reads '' as null. Only OTHER carries
						// one — every other purpose names itself.
						purposeOther:
							purpose === OrderQuotePurpose.Other ? purposeOther.trim() : '',
						deadline: deadline?.toISOString() || undefined,
						// Only when it actually moved: the backend re-snapshots the
						// quote's payment settings from the new client whenever this
						// field changes, so sending it unchanged is not a no-op.
						...(organizationId !== (initialValues.organizationId ?? '')
							? { organizationId }
							: {}),
					},
				},
			});
			toast.success('Details updated');
			onOpenChange(false);
			onSuccess();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update');
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Edit details</DialogTitle>
				</DialogHeader>
				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-1.5">
								<Label>
									Name <span className="text-destructive">*</span>
								</Label>
								<Input value={name} onChange={(e) => setName(e.target.value)} />
							</div>
							<div className="flex flex-col gap-1.5">
								<Label>Description</Label>
								<Textarea
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									placeholder="Quote description"
									rows={4}
								/>
							</div>
							<div className="flex flex-col gap-1.5">
								<Label>
									Client <span className="text-destructive">*</span>
								</Label>
								<OrganizationSelector
									value={organizationId}
									onChange={setOrganizationId}
									selectedLabel={initialValues.organizationName ?? undefined}
									disabled={clientLocked}
								/>
								{clientLocked && (
									<span className="text-2xs text-text-4">
										This quote already has offers priced for{' '}
										{initialValues.organizationName ?? 'this client'}. Create a
										new quote to price for someone else.
									</span>
								)}
							</div>
							<div className="flex flex-col gap-1.5">
								<Label>Purpose</Label>
								<SelectPopover
									value={purpose}
									onChange={(next) => {
										setPurpose(next);
										// Dropped rather than kept hidden — the payload is
										// built from state, so a stale answer would save
										// against a purpose that no longer asks the question.
										if (next !== OrderQuotePurpose.Other) setPurposeOther('');
									}}
									options={PURPOSE_OPTIONS}
									placeholder="Select purpose"
								/>
								{purpose === OrderQuotePurpose.Other && (
									<Input
										value={purposeOther}
										onChange={(e) => setPurposeOther(e.target.value)}
										placeholder="Describe the purpose"
										className="mt-1.5"
									/>
								)}
							</div>
							<div className="flex flex-col gap-1.5">
								<Label>Deadline</Label>
								<DatePicker
									value={deadline}
									onChange={(d) => setDeadline(d ?? null)}
									placeholder="Select date"
									minDate={new Date()}
								/>
							</div>
						</div>
					</DialogBody>
					<DialogFooter>
						<Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
							Cancel
						</Button>
						<Button type="submit" disabled={!name.trim() || !organizationId || loading}>
							{loading ? 'Saving…' : 'Save'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
